import { Card, Table, Button, Space, Typography, Input, Select, Switch, Tag, Popconfirm, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { usePersonFieldAdmin } from '../../hooks/use_person_field_admin';

const { Title, Text } = Typography;

const KEY_MAX = 64; // backend _KEY_RE: ^[a-z][a-z0-9_]{0,63}$

// Types offerable for a new field. 'geo_multi' is deliberately absent — it is
// no longer offered, but stays in TYPE_LABELS so fields created earlier (and
// the backend enum) still render with a proper name.
const TYPE_OPTIONS = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'geo', label: 'Geo Location' },
    { value: 'select', label: 'Dropdown (custom list)' },
];

const TYPE_LABELS = {
    ...Object.fromEntries(TYPE_OPTIONS.map(o => [o.value, o.label])),
    geo_multi: 'Geo Locations (multiple)',
};

// Built-in Person fields, not rows in person_field_definitions. Shown here
// read-only so admins can see the full set of Person fields — default and
// custom — in one place. `name` is a column on Entity; description is
// the entity's default EntityDescription row, which the create/edit form and
// detail page both surface as a plain field. Entity.url and Entity.email
// both exist but are not offered on people — see CreateEntityForm. An
// imported email still lives on the record and still drives "Send email
// invite"; it is simply not a field of a person's profile.
const BUILT_IN_FIELDS = [
    { key: 'name', label: 'Name', field_type: 'text' },
    { key: 'description', label: 'Description', field_type: 'text' },
].map(f => ({ ...f, id: `built_in:${f.key}`, built_in: true, enabled: true }));

/**
 * Tags input for a dropdown field's options. Enter or a comma adds the typed
 * option (antd's tags mode); Tab does too, so a list can be typed in one go —
 * with nothing typed, Tab still moves focus as usual.
 */
function OptionsInput({ value, onChange, ...rest }) {
    const [search, setSearch] = useState('');
    const addTyped = () => {
        const option = search.trim();
        setSearch('');
        if (!option || value.some(o => o.toLowerCase() === option.toLowerCase())) return;
        onChange([...value, option]);
    };
    return (
        <Select
            mode="tags"
            value={value}
            // searchValue is controlled (for Tab), so antd won't clear it itself
            // after Enter/comma adds a tag — clear it on every change.
            onChange={v => { setSearch(''); onChange(v); }}
            searchValue={search}
            onSearch={setSearch}
            onInputKeyDown={e => {
                if (e.key === 'Tab' && search.trim()) {
                    e.preventDefault();
                    addTyped();
                }
            }}
            onBlur={addTyped} // don't drop a half-typed option when clicking away
            tokenSeparators={[',']}
            open={false}
            suffixIcon={null}
            {...rest}
        />
    );
}

/**
 * The Entity.attributes key a new field's values are stored under, derived
 * from its label so admins never deal with it ("Shirt Size" -> shirt_size).
 * Keys are fixed once created (the backend won't rename them — that would
 * orphan saved values), so this only ever runs at creation time. A label that
 * slugs to a taken key gets a numeric suffix rather than being rejected.
 */
function keyFromLabel(label, taken) {
    const slug = label
        .normalize('NFKD').replace(/\p{M}/gu, '') // "Café" -> "Cafe"
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    // Keys must start with a letter; digit-first or non-Latin labels get a
    // prefix instead of an error.
    const base = (/^[a-z]/.test(slug) ? slug : `field_${slug}`.replace(/_+$/, '')).slice(0, KEY_MAX);
    let key = base;
    for (let n = 2; taken.has(key); n += 1) {
        const suffix = `_${n}`;
        key = base.slice(0, KEY_MAX - suffix.length) + suffix;
    }
    return key;
}

/**
 * Superadmin-only panel to define custom Person entity fields. Values still
 * live in Entity.attributes — this panel only manages the field schema
 * (label/type/enabled; the storage key is derived). Renders nothing for
 * non-superadmins (the list endpoint 403s), so it is safe to mount
 * unconditionally.
 */
function PersonFields() {
    const { fields, isServerAdmin, loading, create, update, remove } = usePersonFieldAdmin();
    const [newLabel, setNewLabel] = useState('');
    const [newType, setNewType] = useState('text');
    const [newOptions, setNewOptions] = useState([]); // 'select' only
    const [adding, setAdding] = useState(false);
    const [busyId, setBusyId] = useState(null);

    if (!isServerAdmin) return null;

    // Built-ins first, then custom fields in their configured order — one
    // table shows the full set of Person fields regardless of where they live.
    const dataSource = [...BUILT_IN_FIELDS, ...fields];

    const handleAdd = () => {
        const label = newLabel.trim();
        if (!label) return;
        // Two fields sharing a label would render indistinguishably in the
        // create/edit form, so catch it here rather than via a key suffix.
        if (dataSource.some(f => f.label.toLowerCase() === label.toLowerCase())) {
            message.error(`A field labelled "${label}" already exists`);
            return;
        }
        if (newType === 'select' && newOptions.length === 0) {
            message.error('Add at least one option for a dropdown field');
            return;
        }
        const key = keyFromLabel(label, new Set(dataSource.map(f => f.key)));
        setAdding(true);
        create({ key, label, field_type: newType, ...(newType === 'select' && { options: newOptions }) })
            .then(() => { message.success('Field added'); setNewLabel(''); setNewType('text'); setNewOptions([]); })
            .catch(err => message.error(err?.message || 'Could not add field'))
            .finally(() => setAdding(false));
    };

    const handleOptionsChange = (row, options) => {
        if (options.length === 0) {
            message.error('A dropdown field needs at least one option');
            return;
        }
        setBusyId(row.id);
        update(row.id, { options })
            .catch(err => message.error(err?.message || 'Could not update options'))
            .finally(() => setBusyId(null));
    };

    const handleToggle = (row, enabled) => {
        setBusyId(row.id);
        update(row.id, { enabled })
            .catch(() => message.error('Could not update field'))
            .finally(() => setBusyId(null));
    };

    const handleDelete = row => {
        setBusyId(row.id);
        return remove(row.id)
            .then(() => message.success(`Deleted "${row.label}"`))
            .catch(() => message.error('Could not delete field'))
            .finally(() => setBusyId(null));
    };

    const columns = [
        { title: 'Label', dataIndex: 'label', key: 'label' },
        {
            title: 'Type',
            dataIndex: 'field_type',
            key: 'field_type',
            render: t => <Tag>{TYPE_LABELS[t] || t}</Tag>,
        },
        {
            title: 'Options',
            key: 'options',
            // Dropdown fields only. Order here is the order people pick from
            // and the Home table sorts by. Removing an option leaves values
            // already saved with it untouched.
            render: (_, row) => row.field_type === 'select' ? (
                <OptionsInput
                    size="small"
                    value={row.options || []}
                    onChange={opts => handleOptionsChange(row, opts)}
                    loading={busyId === row.id}
                    placeholder="Type an option, Tab to add more"
                    style={{ minWidth: 200 }}
                />
            ) : null,
        },
        {
            title: 'Source',
            key: 'source',
            render: (_, row) => row.built_in
                ? <Tag color="default">Built-in</Tag>
                : <Tag color="blue">Custom</Tag>,
        },
        {
            title: 'Enabled',
            key: 'enabled',
            render: (_, row) => row.built_in
                ? <Text type="secondary">Always on</Text>
                : (
                    <Switch
                        checked={row.enabled}
                        loading={busyId === row.id}
                        onChange={checked => handleToggle(row, checked)}
                    />
                ),
        },
        {
            title: '',
            key: 'actions',
            align: 'right',
            render: (_, row) => row.built_in ? null : (
                <Popconfirm
                    title={`Delete "${row.label}"?`}
                    description="This also removes every value saved for this field on Person entities. To keep the data, disable the field instead."
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleDelete(row)}
                >
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        loading={busyId === row.id}
                        aria-label={`Delete ${row.label}`}
                    />
                </Popconfirm>
            ),
        },
    ];

    return (
        <Card style={{ marginTop: 16 }}>
            <Title level={5} style={{ marginTop: 0 }}>Person fields</Title>
            <Text type="secondary">
                All fields available on Person entities: built-in fields (always
                present, not editable here) and custom fields your node has added.
                Custom field values are stored in the entity's attributes, but
                render as first-class fields (like Description) in the create/edit form
                and detail page. Disabling a custom field hides it from the UI
                without deleting any data already saved for it; deleting it
                removes the field and its saved values permanently.
            </Text>
            <Space style={{ marginTop: 16, marginBottom: 16 }} wrap>
                <Input
                    placeholder="e.g. Hometown"
                    value={newLabel}
                    onChange={e => setNewLabel(e.target.value)}
                    onPressEnter={handleAdd}
                    style={{ width: 240 }}
                />
                <Select value={newType} onChange={setNewType} options={TYPE_OPTIONS} style={{ width: 200 }} />
                {newType === 'select' && (
                    <OptionsInput
                        value={newOptions}
                        onChange={setNewOptions}
                        placeholder="Options, e.g. Fall 2022 — press Tab to add multiple"
                        style={{ minWidth: 320 }}
                    />
                )}
                <Button type="primary" onClick={handleAdd} loading={adding} disabled={!newLabel.trim() || (newType === 'select' && newOptions.length === 0)}>Add</Button>
            </Space>
            <Table
                rowKey="id"
                size="small"
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={false}
                locale={{ emptyText: 'No custom Person fields yet' }}
            />
        </Card>
    );
}

export default PersonFields;
